import React, { useEffect, useState } from 'react';

import { Text, Title, Group, Divider, Loader } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import PieChart, { ChartData } from 'components/charts/PieChart';
import { useAuth } from 'context/auth-context';
import usePackageRevenue from 'hooks/reports/revenue/usePackageRevenue';
import { useNavigate } from 'react-router-dom';
import { getMonthRangeDatesMTD } from 'utils/date-functions';

import { useDashboardStyles } from '../styles';

const PackageRevenueCard = (): JSX.Element => {
  const { classes: dashboard } = useDashboardStyles();
  const navigate = useNavigate();

  const { hasPerm } = useAuth();
  const hasPermission = hasPerm('PackageReportsView');

  const [monthStart, todayEnd] = getMonthRangeDatesMTD();
  const [tableData, setTableData] = useState<ChartData[]>([]);

  const { data, isLoading, isError, isSuccess } = usePackageRevenue({
    dateStart: monthStart.toISOString(),
    dateEnd: todayEnd.toISOString(),
    limit: '100',
    ordering: '-revenue',
    enabled: hasPermission,
  });

  useEffect(() => {
    if (!isLoading && data) {
      const flatData = data.pages
        .map(page => page.data.results)
        .flat()
        .map(p => ({ label: p.package, value: p.revenue / 100 }));

      if (flatData.length > 5) {
        const top5 = flatData.slice(0, 5);
        const allOtherSum = flatData.slice(5).reduce((prev, curr) => prev + curr.value, 0);
        setTableData([...top5, { label: 'All Others', value: allOtherSum }]);
      } else {
        setTableData(flatData);
      }
    }
  }, [isLoading, data, setTableData]);

  return (
    <div
      className={`${dashboard.package} ${dashboard.card} ${
        hasPermission ? dashboard.clickableCard : undefined
      }`}
      onClick={() => {
        if (hasPermission) {
          navigate(`reports/package-revenue`);
        }
      }}
    >
      <div className={dashboard.cardHeader}>
        <Title order={4}>Package Revenue MTD</Title>
      </div>

      <Divider />

      {!hasPermission && (
        <Group position='center' py='xl'>
          <IconLock size={'3rem'} />
        </Group>
      )}

      {isLoading && (
        <Group position='center' py='xl'>
          <Loader size='lg' />
        </Group>
      )}

      {isError && (
        <Group position='center' py='xl'>
          <Text className={dashboard.value}>Error</Text>
        </Group>
      )}

      {isSuccess && tableData.length > 0 && <PieChart data={tableData} />}

      {isSuccess && tableData.length === 0 && (
        <Group position='center' py='xl'>
          <Text className={dashboard.value}>No data</Text>
        </Group>
      )}
    </div>
  );
};

export default PackageRevenueCard;
